/**
 * SURFER ASSETS & VECTOR FALLBACKS (DYNAMIC ID: 51)
 * Colección de recursos gráficos, fallbacks SVG vectoriales ultra optimizados y utilidades
 * de precarga para las Pantallas Gigantes de Estadio (/screen) de Esquivador (Surfer).
 * 
 * @module components/Screen/Sections/Surfer/SurferAssets
 */

/**
 * Resuelve una URL de recurso multimedia relativa anteponiendo la base del API si es necesario.
 * @param {string|null} url - Ruta del recurso proporcionada por backend o tema.
 * @param {string} [fallback=''] - Recurso de respaldo por defecto.
 * @returns {string} URL normalizada lista para renderizado.
 */
export const resolveAssetUrl = (url, fallback = '') => {
    if (!url || typeof url !== 'string') return fallback || '';
    const cleanUrl = url.trim();
    if (!cleanUrl) return fallback || '';

    if (/^(javascript|vbscript|data:(?!image\/))/i.test(cleanUrl)) {
        return fallback || '';
    }

    if (
        cleanUrl.startsWith('http://') ||
        cleanUrl.startsWith('https://') ||
        cleanUrl.startsWith('data:image/') ||
        cleanUrl.startsWith('blob:')
    ) {
        return cleanUrl;
    }

    const apiBase = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const backendOrigin = apiBase.replace(/\/api\/?$/i, '');
    const normalizedPath = cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
    return `${backendOrigin}${normalizedPath}`;
};

/**
 * Precarga de forma asíncrona un conjunto de URLs en memoria RAM del navegador.
 * @param {Array<string>} [urls=[]] - Colección de URLs a precargar.
 * @returns {Promise<Array<{url: string, success: boolean}>>}
 */
export const preloadImageUrls = (urls = []) => {
    if (!Array.isArray(urls)) return Promise.resolve([]);
    const cleanUrls = Array.from(new Set(
        urls
            .filter((u) => typeof u === 'string' && u.trim().length > 0)
            .map((u) => resolveAssetUrl(u))
    ));

    const loadPromises = cleanUrls.map((url) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve({ url, success: true, image: img });
            img.onerror = () => resolve({ url, success: false, image: null });
            img.src = url;
        });
    });

    return Promise.all(loadPromises);
};

// =========================================================================
// FALLBACKS SVG VECTORIALES INMUTABLES PARA RENDERIZADO 2D / 60 FPS
// =========================================================================

/**
 * Fondo Cyberpunk / Synthwave de Estadio para Esquivador.
 */
export const DEFAULT_BACKGROUND_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1920 1080" width="1920" height="1080">
  <defs>
    <linearGradient id="skyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="%23050014" />
      <stop offset="45%" stop-color="%231a0033" />
      <stop offset="75%" stop-color="%233b0066" />
      <stop offset="100%" stop-color="%230a001a" />
    </linearGradient>
    <radialGradient id="sunGlow" cx="50%" cy="55%" r="45%">
      <stop offset="0%" stop-color="%23ff007f" stop-opacity="0.9" />
      <stop offset="40%" stop-color="%23ff7700" stop-opacity="0.6" />
      <stop offset="80%" stop-color="%2300ffcc" stop-opacity="0.2" />
      <stop offset="100%" stop-color="transparent" stop-opacity="0" />
    </radialGradient>
    <linearGradient id="sunGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="%23ffe600" />
      <stop offset="50%" stop-color="%23ff007f" />
      <stop offset="100%" stop-color="%237700aa" />
    </linearGradient>
    <pattern id="gridPattern" width="60" height="40" patternUnits="userSpaceOnUse">
      <line x1="0" y1="0" x2="60" y2="0" stroke="%2300ffcc" stroke-width="1.5" stroke-opacity="0.3" />
      <line x1="0" y1="0" x2="0" y2="40" stroke="%2300ffcc" stroke-width="1.5" stroke-opacity="0.3" />
    </pattern>
  </defs>
  <rect width="1920" height="1080" fill="url(%23skyGrad)" />
  <circle cx="960" cy="520" r="340" fill="url(%23sunGlow)" />
  <circle cx="960" cy="520" r="220" fill="url(%23sunGrad)" opacity="0.85" />
  <!-- Neon skyline -->
  <polygon points="100,560 160,380 220,560 280,450 350,560 480,320 540,560 700,420 800,560 1120,560 1220,400 1340,560 1460,330 1560,560 1680,440 1780,560 1920,380 1920,560 0,560" fill="%230e0422" opacity="0.9" />
  <polygon points="0,560 1920,560 1920,1080 0,1080" fill="%23070014" />
  <rect y="560" width="1920" height="520" fill="url(%23gridPattern)" opacity="0.45" />
</svg>`;

/**
 * Pista / Carretera Neón por defecto (3 carriles con rejilla de velocidad).
 */
export const DEFAULT_ROAD_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 1200" width="800" height="1200">
  <defs>
    <linearGradient id="roadGrad" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="%230e0024" />
      <stop offset="60%" stop-color="%23170538" />
      <stop offset="100%" stop-color="%2324084f" />
    </linearGradient>
    <linearGradient id="edgeGlow" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="%2300ffcc" />
      <stop offset="10%" stop-color="%2300ffcc" stop-opacity="0.3" />
      <stop offset="90%" stop-color="%23ff007f" stop-opacity="0.3" />
      <stop offset="100%" stop-color="%23ff007f" />
    </linearGradient>
  </defs>
  <rect width="800" height="1200" fill="url(%23roadGrad)" />
  <line x1="266" y1="0" x2="266" y2="1200" stroke="%2300ffcc" stroke-width="4" stroke-dasharray="25,20" stroke-opacity="0.7" />
  <line x1="533" y1="0" x2="533" y2="1200" stroke="%23ff007f" stroke-width="4" stroke-dasharray="25,20" stroke-opacity="0.7" />
  <rect x="0" y="0" width="16" height="1200" fill="%2300ffcc" />
  <rect x="784" y="0" width="16" height="1200" fill="%23ff007f" />
</svg>`;

/**
 * Nave / Surfer del Jugador 1 (Neón Cian #00FFCC con propulsor de plasma).
 */
export const DEFAULT_PLAYER1_SURFER_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300" width="300" height="300">
  <defs>
    <linearGradient id="p1Body" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="%23ffffff" />
      <stop offset="35%" stop-color="%2300ffcc" />
      <stop offset="85%" stop-color="%230088aa" />
      <stop offset="100%" stop-color="%23003355" />
    </linearGradient>
    <radialGradient id="p1Thruster" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="%23ffffff" />
      <stop offset="40%" stop-color="%2300ffcc" />
      <stop offset="80%" stop-color="%230088ff" />
      <stop offset="100%" stop-color="transparent" />
    </radialGradient>
    <filter id="p1Glow" x="-30%" y="-30%" width="160%" height="160%">
      <feGaussianBlur stdDeviation="6" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
  <!-- Propulsores y estela -->
  <polygon points="110,230 150,295 190,230" fill="url(%23p1Thruster)" filter="url(%23p1Glow)" opacity="0.9" />
  <polygon points="125,230 150,275 175,230" fill="%23ffffff" filter="url(%23p1Glow)" />
  <!-- Alas y alerones -->
  <polygon points="150,30 270,210 240,240 180,215 150,235 120,215 60,240 30,210" fill="url(%23p1Body)" filter="url(%23p1Glow)" stroke="%2300ffcc" stroke-width="4" />
  <!-- Cabina / Cúpula de energía -->
  <polygon points="150,60 185,160 150,195 115,160" fill="%23002233" stroke="%23ffffff" stroke-width="3" />
  <ellipse cx="150" cy="140" rx="18" ry="32" fill="%2300ffcc" opacity="0.85" filter="url(%23p1Glow)" />
  <circle cx="150" cy="130" r="7" fill="%23ffffff" />
  <!-- Luces de punta de ala -->
  <circle cx="35" cy="210" r="7" fill="%2300ffcc" filter="url(%23p1Glow)" />
  <circle cx="265" cy="210" r="7" fill="%2300ffcc" filter="url(%23p1Glow)" />
  <text x="150" y="222" fill="%23ffffff" font-family="Arial, sans-serif" font-weight="900" font-size="20" text-anchor="middle" letter-spacing="1">P1</text>
</svg>`;

/**
 * Nave / Surfer del Jugador 2 (Neón Magenta #FF007F con propulsor de plasma).
 */
export const DEFAULT_PLAYER2_SURFER_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300" width="300" height="300">
  <defs>
    <linearGradient id="p2Body" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="%23ffffff" />
      <stop offset="35%" stop-color="%23ff007f" />
      <stop offset="85%" stop-color="%23aa0055" />
      <stop offset="100%" stop-color="%23440022" />
    </linearGradient>
    <radialGradient id="p2Thruster" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="%23ffffff" />
      <stop offset="40%" stop-color="%23ff007f" />
      <stop offset="80%" stop-color="%23ffaa00" />
      <stop offset="100%" stop-color="transparent" />
    </radialGradient>
    <filter id="p2Glow" x="-30%" y="-30%" width="160%" height="160%">
      <feGaussianBlur stdDeviation="6" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
  <!-- Propulsores y estela -->
  <polygon points="110,230 150,295 190,230" fill="url(%23p2Thruster)" filter="url(%23p2Glow)" opacity="0.9" />
  <polygon points="125,230 150,275 175,230" fill="%23ffffff" filter="url(%23p2Glow)" />
  <!-- Alas y alerones -->
  <polygon points="150,30 270,210 240,240 180,215 150,235 120,215 60,240 30,210" fill="url(%23p2Body)" filter="url(%23p2Glow)" stroke="%23ff007f" stroke-width="4" />
  <!-- Cabina / Cúpula de energía -->
  <polygon points="150,60 185,160 150,195 115,160" fill="%23330018" stroke="%23ffffff" stroke-width="3" />
  <ellipse cx="150" cy="140" rx="18" ry="32" fill="%23ff007f" opacity="0.85" filter="url(%23p2Glow)" />
  <circle cx="150" cy="130" r="7" fill="%23ffffff" />
  <!-- Luces de punta de ala -->
  <circle cx="35" cy="210" r="7" fill="%23ff007f" filter="url(%23p2Glow)" />
  <circle cx="265" cy="210" r="7" fill="%23ff007f" filter="url(%23p2Glow)" />
  <text x="150" y="222" fill="%23ffffff" font-family="Arial, sans-serif" font-weight="900" font-size="20" text-anchor="middle" letter-spacing="1">P2</text>
</svg>`;

/**
 * Obstáculo por defecto: Barrera Láser Cyberpunk con espinas magnéticas y advertencia de alto voltaje.
 */
export const DEFAULT_OBSTACLE_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 200" width="260" height="200">
  <defs>
    <linearGradient id="barGrad" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="%23ff1100" />
      <stop offset="50%" stop-color="%23ffcc00" />
      <stop offset="100%" stop-color="%23ff1100" />
    </linearGradient>
    <filter id="obsGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="5" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
  <!-- Postes laterales -->
  <rect x="15" y="40" width="30" height="150" rx="6" fill="%2322222b" stroke="%23ff3300" stroke-width="3" />
  <rect x="215" y="40" width="30" height="150" rx="6" fill="%2322222b" stroke="%23ff3300" stroke-width="3" />
  <!-- Luz estroboscópica de peligro -->
  <circle cx="30" cy="35" r="14" fill="%23ff0033" filter="url(%23obsGlow)" />
  <circle cx="230" cy="35" r="14" fill="%23ff0033" filter="url(%23obsGlow)" />
  <!-- Rayos láser y barras de energía -->
  <rect x="35" y="70" width="190" height="28" rx="5" fill="url(%23barGrad)" filter="url(%23obsGlow)" />
  <rect x="35" y="125" width="190" height="28" rx="5" fill="url(%23barGrad)" filter="url(%23obsGlow)" />
  <!-- Rayas de advertencia -->
  <line x1="60" y1="70" x2="80" y2="98" stroke="%23000000" stroke-width="5" />
  <line x1="100" y1="70" x2="120" y2="98" stroke="%23000000" stroke-width="5" />
  <line x1="140" y1="70" x2="160" y2="98" stroke="%23000000" stroke-width="5" />
  <line x1="180" y1="70" x2="200" y2="98" stroke="%23000000" stroke-width="5" />
  <!-- Emblema de advertencia central -->
  <polygon points="130,95 145,125 115,125" fill="%23ffe600" stroke="%23000000" stroke-width="2" />
  <text x="130" y="122" font-family="Arial, sans-serif" font-weight="900" font-size="14" fill="%23000" text-anchor="middle">!</text>
</svg>`;

/**
 * Moneda por defecto: Moneda dorada arcade giratoria con borde brillante y estrella (+50 pts).
 */
export const DEFAULT_COIN_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 160" width="160" height="160">
  <defs>
    <radialGradient id="coinGrad" cx="35%" cy="35%" r="65%">
      <stop offset="0%" stop-color="%23ffffff" />
      <stop offset="25%" stop-color="%23ffe600" />
      <stop offset="70%" stop-color="%23ff9900" />
      <stop offset="100%" stop-color="%23cc6600" />
    </radialGradient>
    <filter id="coinGlow" x="-25%" y="-25%" width="150%" height="150%">
      <feGaussianBlur stdDeviation="4" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
  <circle cx="80" cy="80" r="70" fill="url(%23coinGrad)" filter="url(%23coinGlow)" stroke="%23ffffff" stroke-width="4" />
  <circle cx="80" cy="80" r="54" fill="none" stroke="%23ffe600" stroke-width="3" stroke-dasharray="8,5" />
  <!-- Estrella arcade -->
  <polygon points="80,42 91,65 116,68 97,85 102,110 80,97 58,110 63,85 44,68 69,65" fill="%23ffffff" filter="url(%23coinGlow)" />
</svg>`;

/**
 * Bonus Especial por defecto: Diamante hiper-energético (+500 pts).
 */
export const DEFAULT_BONUS_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 180 180" width="180" height="180">
  <defs>
    <linearGradient id="bonusGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="%23ffffff" />
      <stop offset="30%" stop-color="%2300ffea" />
      <stop offset="70%" stop-color="%23ff00ea" />
      <stop offset="100%" stop-color="%237b00ff" />
    </linearGradient>
    <filter id="bonusGlow" x="-30%" y="-30%" width="160%" height="160%">
      <feGaussianBlur stdDeviation="6" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
  <!-- Forma de gema de poder -->
  <polygon points="90,15 160,65 140,155 90,175 40,155 20,65" fill="url(%23bonusGrad)" filter="url(%23bonusGlow)" stroke="%23ffffff" stroke-width="4" />
  <polygon points="90,15 125,65 90,95 55,65" fill="%23ffffff" opacity="0.6" />
  <polygon points="90,95 140,155 90,175 40,155" fill="%23ffffff" opacity="0.3" />
  <text x="90" y="125" font-family="Arial, sans-serif" font-weight="900" font-size="24" fill="%23ffffff" text-anchor="middle" letter-spacing="1">500</text>
</svg>`;

/**
 * Cockpit Overlay por defecto: Marco futurista de cabina con visor holográfico.
 */
export const DEFAULT_COCKPIT_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" width="800" height="600">
  <defs>
    <linearGradient id="cockpitGlass" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="%2300ffff" stop-opacity="0.05" />
      <stop offset="100%" stop-color="%2300ffff" stop-opacity="0.15" />
    </linearGradient>
  </defs>
  <rect width="800" height="600" fill="url(%23cockpitGlass)" />
  <!-- Esquinas del visor HUD -->
  <path d="M 40 100 L 40 40 L 100 40" fill="none" stroke="%2300ffcc" stroke-width="4" />
  <path d="M 760 100 L 760 40 L 700 40" fill="none" stroke="%2300ffcc" stroke-width="4" />
  <path d="M 40 500 L 40 560 L 100 560" fill="none" stroke="%2300ffcc" stroke-width="4" />
  <path d="M 760 500 L 760 560 L 700 560" fill="none" stroke="%2300ffcc" stroke-width="4" />
  <!-- Líneas de horizonte HUD -->
  <line x1="280" y1="300" x2="360" y2="300" stroke="%2300ffcc" stroke-width="2" opacity="0.7" />
  <line x1="440" y1="300" x2="520" y2="300" stroke="%2300ffcc" stroke-width="2" opacity="0.7" />
  <circle cx="400" cy="300" r="12" fill="none" stroke="%2300ffcc" stroke-width="2" opacity="0.7" />
</svg>`;

/**
 * Trofeo de Ganador de Estadio (Podio de Victoria).
 */
export const DEFAULT_TROPHY_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 360" width="320" height="360">
  <defs>
    <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="%23ffffff" />
      <stop offset="25%" stop-color="%23ffe600" />
      <stop offset="60%" stop-color="%23ff9900" />
      <stop offset="100%" stop-color="%23b36200" />
    </linearGradient>
    <filter id="trophyGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="8" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
  <!-- Asas del trofeo -->
  <path d="M 70 80 C 10 90 10 180 80 190" fill="none" stroke="url(%23goldGrad)" stroke-width="14" stroke-linecap="round" />
  <path d="M 250 80 C 310 90 310 180 240 190" fill="none" stroke="url(%23goldGrad)" stroke-width="14" stroke-linecap="round" />
  <!-- Copa principal -->
  <path d="M 70 50 L 250 50 C 250 160 210 220 160 230 C 110 220 70 160 70 50 Z" fill="url(%23goldGrad)" filter="url(%23trophyGlow)" stroke="%23ffffff" stroke-width="4" />
  <!-- Tallo y Base -->
  <path d="M 140 230 L 180 230 L 175 280 L 145 280 Z" fill="url(%23goldGrad)" stroke="%23b36200" stroke-width="3" />
  <rect x="90" y="280" width="140" height="45" rx="10" fill="%2322222b" stroke="url(%23goldGrad)" stroke-width="4" />
  <!-- Estrella central -->
  <polygon points="160,90 169,112 192,114 174,130 179,152 160,140 141,152 146,130 128,114 151,112" fill="%23ffffff" filter="url(%23trophyGlow)" />
</svg>`;
